import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AgentInvoiceService, AgentInvoiceDto, AgentTransaction, InvoiceChargeSummary } from '../../../services/agent-invoice.service';
import { ExternalDataService, Agent } from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-agent-invoices',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './agent-invoices.component.html',
  styleUrl: './agent-invoices.component.css'
})
export class AgentInvoicesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  invoices: AgentInvoiceDto[] = [];
  agents: Agent[] = [];

  // UI state
  loading = false;
  generating = false;
  errorMessage = '';
  successMessage = '';
  searchText = '';

  // Pagination state
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;
  
  // Modal state
  showGenerateModal = false;
  selectedAgentId = '';
  selectedToDate = '';
  uninvoicedTransactions: AgentTransaction[] = [];
  loadingTransactions = false;
  totalPendingAmount = 0;
  roundOffAmount = 0;

  // Make Math available in template
  Math = Math;

  // Expansion state
  expandedInvoiceId: string | null = null;

  toggleExpandInvoice(invoiceId: string): void {
    this.expandedInvoiceId = this.expandedInvoiceId === invoiceId ? null : invoiceId;
  }

  isExpanded(invoiceId: string): boolean {
    return this.expandedInvoiceId === invoiceId;
  }

  getChargeSummary(invoice: AgentInvoiceDto): InvoiceChargeSummary[] {
    if (invoice.chargeSummary) {
      return invoice.chargeSummary;
    }
    const summary = this.calculateChargeSummary(invoice.transactions);
    invoice.chargeSummary = summary;
    return summary;
  }

  private calculateChargeSummary(transactions?: AgentTransaction[]): InvoiceChargeSummary[] {
    if (!transactions || transactions.length === 0) {
      return [];
    }

    const summaryMap = new Map<string, InvoiceChargeSummary>();

    for (const tx of transactions) {
      const type = tx.type || (tx as any).Type || 'Other';
      const amount = Number(tx.amount ?? (tx as any).Amount ?? 0);
      const cgst = Number(tx.cgst ?? (tx as any).Cgst ?? (tx as any).CGST ?? 0);
      const sgst = Number(tx.sgst ?? (tx as any).Sgst ?? (tx as any).SGST ?? 0);
      const total = Number(tx.totalAmount ?? (tx as any).TotalAmount ?? 0);

      const existing = summaryMap.get(type);
      if (existing) {
        existing.count += 1;
        existing.taxableAmount += amount;
        existing.cgst += cgst;
        existing.sgst += sgst;
        existing.totalAmount += total;
      } else {
        summaryMap.set(type, {
          type,
          count: 1,
          taxableAmount: amount,
          cgst,
          sgst,
          totalAmount: total
        });
      }
    }

    return Array.from(summaryMap.values()).map(summary => ({
      ...summary,
      taxableAmount: Math.round(summary.taxableAmount * 100) / 100,
      cgst: Math.round(summary.cgst * 100) / 100,
      sgst: Math.round(summary.sgst * 100) / 100,
      totalAmount: Math.round(summary.totalAmount * 100) / 100
    }));
  }

  getTypeLabel(type: string): string {
    if (!type) return '—';
    const labels: { [key: string]: string } = {
      PlatformCharge: 'Platform Charge',
      LeadGenerationFee: 'Lead Generation Fee',
      VoucherSettlement: 'Voucher Settlement',
      SubscriptionSettlement: 'Subscription Settlement',
    };
    return labels[type] || type.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  constructor(
    private agentInvoiceService: AgentInvoiceService,
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadAgents();
        this.loadInvoices();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgents(): void {
    this.externalDataService.getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents.sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        }
      });
  }

  loadInvoices(): void {
    this.loading = true;
    this.errorMessage = '';
    
    const pageIndex = this.currentPage - 1;
    this.agentInvoiceService.getAgentInvoices(this.searchText?.trim() || undefined, pageIndex, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.invoices = response.items || [];
          this.totalCount = response.totalCount || 0;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.errorMessage = 'Failed to load invoices. Please try again.';
          this.loading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadInvoices();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadInvoices();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadInvoices();
  }

  clearSearch(): void {
    this.searchText = '';
    this.currentPage = 1;
    this.loadInvoices();
  }

  openGenerateModal(): void {
    this.selectedAgentId = '';
    this.selectedToDate = '';
    this.showGenerateModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeGenerateModal(): void {
    this.showGenerateModal = false;
    this.selectedAgentId = '';
    this.selectedToDate = '';
    this.uninvoicedTransactions = [];
    this.totalPendingAmount = 0;
    this.roundOffAmount = 0;
  }

  onAgentSelected(): void {
    if (!this.selectedAgentId) {
      this.uninvoicedTransactions = [];
      this.totalPendingAmount = 0;
      this.roundOffAmount = 0;
      return;
    }

    this.loadingTransactions = true;
    this.errorMessage = '';
    this.uninvoicedTransactions = [];

    this.agentInvoiceService.getUninvoicedTransactions(this.selectedAgentId, this.selectedToDate || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transactions) => {
          this.uninvoicedTransactions = transactions;
          const rawTotal = transactions.reduce((sum, tx) => sum + (tx.totalAmount || 0), 0);
          this.totalPendingAmount = Math.ceil(rawTotal);
          this.roundOffAmount = Number((this.totalPendingAmount - rawTotal).toFixed(2));
          this.loadingTransactions = false;
        },
        error: (error) => {
          console.error('Error loading transactions:', error);
          this.errorMessage = 'Failed to load pending transactions for this agent.';
          this.loadingTransactions = false;
        }
      });
  }

  generateInvoice(): void {
    if (!this.selectedAgentId || this.generating) return;

    this.generating = true;
    this.errorMessage = '';
    
    this.agentInvoiceService.generateAgentInvoice(this.selectedAgentId, this.selectedToDate || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (invoice) => {
          this.generating = false;
          this.showGenerateModal = false;
          this.successMessage = `Successfully generated invoice ${invoice.invoiceId} for ${invoice.agentName}.`;
          this.currentPage = 1;
          this.loadInvoices();
          
          // Clear success message after 5 seconds
          setTimeout(() => this.successMessage = '', 5000);
        },
        error: (error) => {
          console.error('Error generating invoice:', error);
          let message = '';
          if (typeof error.error === 'string') {
            message = error.error;
          } else {
            message = error.error?.detail || error.error?.message || 'Failed to generate invoice. The agent might not have any uninvoiced transactions.';
          }

          // If the message contains the agent ID, replace it with the agent name
          if (message.includes(this.selectedAgentId)) {
            const agent = this.agents.find(a => a.agentId === this.selectedAgentId);
            if (agent) {
              message = message.replace(this.selectedAgentId, agent.name);
            }
          }

          this.errorMessage = message;
          this.generating = false;
        }
      });
  }

  downloadInvoice(invoice: AgentInvoiceDto): void {
    if (invoice.invoiceDownloadUrl) {
      window.open(invoice.invoiceDownloadUrl, '_blank');
    } else {
      this.errorMessage = 'Download URL is not available for this invoice.';
      setTimeout(() => this.errorMessage = '', 5000);
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  calculateSubtotal(): number {
    return this.uninvoicedTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);
  }

  calculateTotalCgst(): number {
    return this.uninvoicedTransactions.reduce((sum, tx) => sum + (tx.cgst || 0), 0);
  }

  calculateTotalSgst(): number {
    return this.uninvoicedTransactions.reduce((sum, tx) => sum + (tx.sgst || 0), 0);
  }

  calculateRawTotal(): number {
    return this.uninvoicedTransactions.reduce((sum, tx) => sum + (tx.totalAmount || 0), 0);
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toFixed(2)}`;
  }
}
